/**
 * Pure data provider for the `timeline[row].event` renderer plugin
 * (the all-day events strip rendered above a calendar row).
 *
 * Walks the row tree starting from a renderer / alias root, finds the
 * nested `calendar-list` aggregate, and returns the full-day events
 * overlapping a date range. Both DOM (`TimelineRowEventRendererPlugin`) and
 * CLI (`renderSheetPlans`) consume this so all-day fixed-child intent
 * lines up across adapters.
 */

import { NaiveDate } from "@tento-chrono";

import type { CalendarEvent } from "../core/dtype.ts";
import { CalendarListEventsProvider } from "./calendar-list-events.ts";
import type {
  FixedChildIntent,
  PluginDataProviders,
  TimelineItemProvider,
} from "./plugin-providers.ts";
import type { ModelRow } from "../rows/model-row.ts";

const PLUGIN_ID = "timeline[row].event";

/**
 * The data provider behind the all-day event renderer (`timeline[row].event`): the all-day events
 * under a row within a range.
 */
export class CalendarAllDayEventsProvider
  implements TimelineItemProvider<CalendarEvent>
{
  readonly pluginId: "timeline[row].event" = PLUGIN_ID;

  // Reuse the calendar-list walker — same row tree, same aggregate
  // facade. The only difference is that all-day events filter to
  // full-day items.
  #inner = new CalendarListEventsProvider();

  // ── TimelineItemProvider ───────────────────────────────────────────

  items(row: ModelRow, range: NaiveDate.Range): readonly CalendarEvent[] {
    return this.#inner.allDayEventsInRange(row, range);
  }

  dateRange(event: CalendarEvent): NaiveDate.Range {
    return this.#inner.eventDateRange(event);
  }

  itemId(event: CalendarEvent): string {
    return event.id;
  }

  // ── Pure FixedChildIntent producer ────────────────────────────────

  fixedChildren(
    row: ModelRow,
    range: NaiveDate.Range,
  ): readonly FixedChildIntent[] {
    return this.items(row, range).map((event) => ({
      id: event.id,
      range: this.dateRange(event),
      align: "start",
      fillWidth: true,
      isOverflow: false,
      pluginId: PLUGIN_ID,
    }));
  }
}

/** `dataProvider` factory for the catalog. */
export function calendarAllDayDataProvider(): PluginDataProviders<CalendarEvent, CalendarEvent> {
  const provider = new CalendarAllDayEventsProvider();
  return {
    timelineItems: provider,
    fixedChildren: (row, range) => provider.fixedChildren(row, range),
  };
}
